import React from 'react';
import { Link } from 'react-router-dom';
import { essencePoints } from '../utils/mockData';
import { useSection } from '../contexts/ContentContext';
import SEO from '../components/SEO';

const AboutUsPage = () => {
  const data = useSection('about', {
    eyebrow: 'About Us',
    heading: 'A House of Silk & Sentiment',
    leadParagraph: 'Silk & Sentiment is a celebration of timeless elegance, where every thread tells a story. Rooted in craftsmanship and driven by emotion, we blend traditional artistry with contemporary design to create pieces that are not just worn, but felt.',
    philosophyHeading: 'Heritage, Reimagined',
    philosophyText: 'At Silk & Sentiment, we craft timeless couture through the finest fabrics and exquisite hand artistry.',
    philosophyQuote: 'Not merely worn, but felt.',
    essenceHeading: 'What We Stand For',
    essence: essencePoints.map((p, i) => ({ number: String(i + 1).padStart(2, '0'), text: p })),
    ctaHeading: 'Begin Your Sentiment',
    ctaText: 'Every journey starts with a conversation. Tell us about the moment you wish to honour, and our atelier will craft a piece as singular as the story behind it.',
    ctaPrimary: 'Visit the Atelier',
    ctaSecondary: 'Enter the Craft Gallery',
  });
  const essenceItems = (data.essence && data.essence.length ? data.essence : essencePoints.map((p, i) => ({ number: String(i + 1).padStart(2, '0'), text: p })));

  return (
    <section style={styles.page} data-testid="about-page">
      <SEO
        title={data.heading || 'About Us'}
        description={data.leadParagraph}
        path="/about"
        type="article"
      />
      <div className="luxury-container" style={styles.container}>
        {/* Hero */}
        <header style={styles.hero}>
          <p style={styles.eyebrow}>{data.eyebrow}</p>
          <h1 className="heading-1" style={styles.title}>
            {data.heading}
          </h1>
          <div style={styles.divider}></div>
          <p style={styles.lead}>
            {data.leadParagraph}
          </p>
        </header>

        {/* Philosophy */}
        <div style={styles.philosophyGrid}>
          <div style={styles.philosophyBlock}>
            <p style={styles.blockLabel}>Our Philosophy</p>
            <h2 className="heading-2" style={styles.blockTitle}>
              {data.philosophyHeading}
            </h2>
            {(data.philosophyText || '').split('\n\n').map((p, i) => (
              <p key={i} style={styles.blockBody}>{p}</p>
            ))}
          </div>

          <aside style={styles.quoteCard}>
            <span style={styles.quoteMark}>&ldquo;</span>
            <p style={styles.quote}>
              {data.philosophyQuote}
            </p>
            <p style={styles.quoteAttr}>— The Atelier</p>
          </aside>
        </div>

        {/* Essence */}
        <div style={styles.essenceSection}>
          <div style={styles.essenceHeader}>
            <p style={styles.eyebrow}>The Essence</p>
            <h2 className="heading-2" style={styles.essenceTitle}>
              {data.essenceHeading}
            </h2>
          </div>

          <div style={styles.essenceGrid}>
            {essenceItems.map((item, i) => (
              <div key={i} style={styles.essenceCard} data-testid={`essence-point-${i}`}>
                <span style={styles.essenceNumber}>
                  {item.number || String(i + 1).padStart(2, '0')}
                </span>
                <p style={styles.essenceText}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={styles.ctaSection}>
          <h2 className="heading-2" style={styles.ctaTitle}>
            {data.ctaHeading}
          </h2>
          <p style={styles.ctaLead}>
            {data.ctaText}
          </p>
          <div style={styles.ctaButtons}>
            <Link to="/contact" className="btn-primary" data-testid="about-contact-cta">
              {data.ctaPrimary}
            </Link>
            <Link to="/design-studio" className="btn-secondary" data-testid="about-studio-cta">
              {data.ctaSecondary}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  page: {
    padding: '140px 0 100px',
    backgroundColor: 'var(--bg-primary)',
    minHeight: '100vh',
  },
  container: {},
  hero: {
    textAlign: 'center',
    maxWidth: '720px',
    margin: '0 auto 100px',
  },
  eyebrow: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: 'var(--accent-gold)',
    marginBottom: '20px',
  },
  title: {
    marginBottom: '24px',
  },
  divider: {
    width: '60px',
    height: '1px',
    background: 'var(--accent-gold)',
    margin: '0 auto 32px',
  },
  lead: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '19px',
    lineHeight: '1.8',
    color: 'var(--text-secondary)',
    fontStyle: 'italic',
  },
  philosophyGrid: {
    display: 'grid',
    gridTemplateColumns: '1.5fr 1fr',
    gap: '80px',
    alignItems: 'center',
    marginBottom: '120px',
  },
  philosophyBlock: {},
  blockLabel: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: 'var(--accent-gold)',
    marginBottom: '16px',
  },
  blockTitle: {
    marginBottom: '28px',
  },
  blockBody: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '15px',
    lineHeight: '1.9',
    color: 'var(--text-secondary)',
    marginBottom: '20px',
  },
  quoteCard: {
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-light)',
    borderRadius: '12px',
    padding: '56px 40px',
    textAlign: 'center',
    position: 'relative',
  },
  quoteMark: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '96px',
    lineHeight: '1',
    color: 'var(--accent-gold)',
    display: 'block',
    marginBottom: '-20px',
  },
  quote: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '28px',
    lineHeight: '1.4',
    color: 'var(--text-primary)',
    marginBottom: '20px',
    fontStyle: 'italic',
  },
  quoteAttr: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: 'var(--accent-gold)',
  },
  essenceSection: {
    marginBottom: '120px',
  },
  essenceHeader: {
    textAlign: 'center',
    marginBottom: '60px',
  },
  essenceTitle: {},
  essenceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '32px',
  },
  essenceCard: {
    background: 'var(--bg-secondary)',
    borderRadius: '12px',
    padding: '40px',
    display: 'flex',
    gap: '24px',
    alignItems: 'flex-start',
    border: '1px solid var(--border-light)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  },
  essenceNumber: {
    flexShrink: 0,
    fontFamily: "'Playfair Display', serif",
    fontSize: '32px',
    color: 'var(--accent-gold)',
    lineHeight: '1',
  },
  essenceText: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '15px',
    lineHeight: '1.8',
    color: 'var(--text-primary)',
  },
  ctaSection: {
    textAlign: 'center',
    maxWidth: '640px',
    margin: '0 auto',
    padding: '60px 40px',
    borderTop: '1px solid var(--border-light)',
  },
  ctaTitle: {
    marginBottom: '20px',
  },
  ctaLead: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '15px',
    lineHeight: '1.8',
    color: 'var(--text-secondary)',
    marginBottom: '40px',
  },
  ctaButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
};

const responsive = document.createElement('style');
responsive.textContent = `
  [data-testid="about-page"] > div > div:nth-child(2) {
    transition: all 0.3s ease;
  }
  [data-testid="about-page"] [data-testid^="essence-point-"]:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(0,0,0,0.06);
  }
  @media (max-width: 900px) {
    [data-testid="about-page"] > div > div:nth-child(2) {
      grid-template-columns: 1fr !important;
      gap: 48px !important;
    }
    [data-testid="about-page"] > div > div:nth-child(3) > div:last-child {
      grid-template-columns: 1fr !important;
    }
  }
`;
document.head.appendChild(responsive);

export default AboutUsPage;
